import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import useDaDataAddress, {
  IDaDataSuggestion,
} from "../../../hooks/useDaDataAddress";
import Input from "../../generic/dataEntry/Input/Input";
import classes from "./AddressInput.module.scss";

interface AddressInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect?: (addressData: {
    fullAddress: string;
    unrestrictedAddress: string;
    city: string;
    cityWithType: string;
    region: string;
    regionWithType: string;
    street: string;
    streetWithType: string;
    postalCode: string | null;
    country: string;
    geoLat: string;
    geoLon: string;
    fiasId: string;
    kladrId: string;
    fullData: IDaDataSuggestion;
  }) => void;
  disabled?: boolean;
  label: string;
  required?: boolean;
  className?: string;
  name?: string;
  showDetailedSuggestions?: boolean;
}

const AddressInputWithSuggestions: React.FC<AddressInputProps> = ({
  value,
  onChange,
  onAddressSelect,
  disabled = false,
  label,
  required = false,
  className = "",
  name = "address",
  showDetailedSuggestions = false,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const suggestionsListRef = useRef<HTMLDivElement>(null);

  const {
    suggestions,
    isLoading,
    error,
    fetchAddressSuggestions,
    clearSuggestions,
  } = useDaDataAddress();

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      onChange(newValue);
      setSelectedIndex(-1);

      if (newValue.length >= 2) {
        fetchAddressSuggestions(newValue);
        setShowSuggestions(true);
      } else {
        clearSuggestions();
        setShowSuggestions(false);
      }
    },
    [onChange, fetchAddressSuggestions, clearSuggestions]
  );

  const handleSuggestionSelect = useCallback(
    (suggestion: IDaDataSuggestion) => {
      const fullAddress = formatSuggestion(suggestion);
      const unrestrictedAddress = suggestion.unrestricted_value;

      setLocalValue(fullAddress);
      onChange(fullAddress);
      setShowSuggestions(false);
      clearSuggestions();
      setSelectedIndex(-1);

      if (onAddressSelect) {
        onAddressSelect({
          fullAddress,
          unrestrictedAddress,
          city: "",
          cityWithType: "",
          region: suggestion.data.region || "",
          regionWithType: suggestion.data.region_with_type || "",
          street: suggestion.data.street || "",
          streetWithType: suggestion.data.street_with_type || "",
          postalCode: suggestion.data.postal_code,
          country: suggestion.data.country,
          geoLat: suggestion.data.geo_lat,
          geoLon: suggestion.data.geo_lon,
          fiasId: suggestion.data.fias_id,
          kladrId: suggestion.data.kladr_id,
          fullData: suggestion,
        });
      }
    },
    [onChange, onAddressSelect, clearSuggestions]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: IDaDataSuggestion) => {
      handleSuggestionSelect(suggestion);
    },
    [handleSuggestionSelect]
  );

  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsListRef.current) {
      const selectedElement = suggestionsListRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatSuggestion = (suggestion: IDaDataSuggestion) => {
    const street =
      suggestion.data.street_with_type || suggestion.data.street || "";
    const house = suggestion.data.house ? `, ${suggestion.data.house}` : "";
    return `${street}${house}`.trim();
  };

  const getSuggestionDetails = useCallback((suggestion: IDaDataSuggestion) => { 
    const details = [];

    if (suggestion.data.country) {
      details.push(suggestion.data.country);
    }

    if (
      suggestion.data.region_with_type &&
      !suggestion.value.includes(suggestion.data.region_with_type)
    ) {
      details.push(suggestion.data.region_with_type);
    }

    return details.join(" • ");
  }, []);

  const renderSuggestions = useMemo(() => {
    if (!showSuggestions) return null;

    if (suggestions.length > 0) {
      return (
        <div className={classes.suggestionsDropdown} ref={suggestionsListRef}>
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.data.fias_id}-${index}`}
              className={`${classes.suggestionItem} ${
                index === selectedIndex ? classes.suggestionItemSelected : ""
              }`}
              onMouseEnter={() => setSelectedIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSuggestionClick(suggestion);
              }}
              title={suggestion.unrestricted_value}
            >
              <div className={classes.suggestionMainText}>
                {formatSuggestion(suggestion)}
              </div>

              {showDetailedSuggestions && (
                <div className={classes.suggestionDetails}>
                  {getSuggestionDetails(suggestion)}
                </div>
              )}

              {!showDetailedSuggestions && suggestion.data.postal_code && (
                <div className={classes.suggestionDetails}>
                  {suggestion.data.postal_code}
                  {suggestion.data.region_with_type &&
                    ` • ${suggestion.data.region_with_type}`}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    return null;
  }, [
    showSuggestions,
    isLoading,
    error,
    suggestions,
    localValue?.length,
    selectedIndex,
    handleSuggestionClick,
    formatSuggestion,
    getSuggestionDetails,
    showDetailedSuggestions,
  ]);

  return (
    <div
      ref={containerRef}
      className={`${classes.addressInputContainer} ${className}`}
    >
      <Input
        name={name}
        value={localValue}
        onChange={handleInputChange}
        disabled={disabled}
        label={label}
        required={required}
        autoComplete="off"
      />

      {renderSuggestions}
    </div>
  );
};

export default AddressInputWithSuggestions;
